import { useState } from "react";
import PropTypes from "prop-types";

function FareDetails({ fareQuoteData, appliedPromo }) {
  const fare = fareQuoteData?.Results?.Fare;
  const fareBreakdown = fareQuoteData?.Results?.FareBreakdown || [];
  let finalTotalFare = fareQuoteData?.final_total_fare_with_taxes || fare?.PublishedFare || 0;
  const promoAmount = appliedPromo?.amount || 0;
  const promoCode = appliedPromo?.code || "";

  let displayPlatformFee = fareQuoteData?.rc_platform_fee || 0;
  const gstRate = fareQuoteData?.rc_gst_rate || 0;
  let displayGstAmount = (displayPlatformFee * gstRate) / 100;
  let remainingDiscountForFlight = promoAmount;
  let effectivePlatformFee = displayPlatformFee;
  let feeDiscountForPlatform = 0;

  if (promoAmount > 0) {
    if (displayPlatformFee > 0) {
      feeDiscountForPlatform = Math.min(displayPlatformFee, promoAmount);
      effectivePlatformFee = displayPlatformFee - feeDiscountForPlatform;
      remainingDiscountForFlight = promoAmount - feeDiscountForPlatform;
      
      displayGstAmount = (effectivePlatformFee * gstRate) / 100;
      
      finalTotalFare = (fare?.PublishedFare || 0) + effectivePlatformFee + displayGstAmount - remainingDiscountForFlight;
      finalTotalFare = Math.max(0, finalTotalFare);
    } else {
      finalTotalFare = fareQuoteData?.total_final_fare_with_taxes_and_discounts || 
                       Math.max(0, finalTotalFare - promoAmount);
    }
  }

  const [showTaxDetails, setShowTaxDetails] = useState(false);

  if (!fare) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
        <div className="text-center text-gray-500 font-medium">Loading fare details...</div>
      </div>
    );
  }

  const totalTravellers = fareBreakdown.reduce((sum, item) => sum + item.PassengerCount, 0);
  const otherCharges = fare?.OtherCharges || 0;
  
  let perPaxOtherCharge = 0;
  if (otherCharges > 0 && totalTravellers > 0) {
    perPaxOtherCharge = otherCharges / totalTravellers;
  }

  const getPassengerLabelPlural = (type, count) => {
    switch (type) {
      case 1: return count > 1 ? "Adults" : "Adult";
      case 2: return count > 1 ? "Children" : "Child";
      case 3: return count > 1 ? "Infants" : "Infant";
      default: return "Passenger";
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-6 w-full lg:w-[360px] border border-gray-100">
      {/* TOTAL PAYABLE SECTION */}
      <div className="mb-2">
        <h4 className="text-[14px] font-bold text-gray-500 tracking-wider mb-2">TOTAL PAYABLE</h4>
        <div className="text-[30px] font-black text-[#FF5B15] leading-none mb-2">
          ₹ {finalTotalFare.toLocaleString()}
        </div>
        <div className="text-[16px] text-gray-500 font-medium">
          {totalTravellers} Traveller{totalTravellers > 1 ? 's' : ''}
        </div>

        {promoAmount > 0 && (
          <div className="bg-[#eef4ea] rounded-xl p-3 mt-4 flex items-center justify-between">
            <div className="flex items-start gap-2.5">
              <svg className="w-5 h-5 text-[#428514] mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <div>
                <div className="bg-[#5a9c2a] text-white text-[12px] font-bold px-2.5 py-0.5 rounded-full inline-block mb-1">
                  {promoCode || 'PROMO'} applied
                </div>
                <div className="text-[#3a7113] text-[14px] font-medium">
                  You saved ₹{promoAmount.toLocaleString()} on this booking
                </div>
              </div>
            </div>
            <div className="text-[#428514] text-[18px] font-bold">
              -₹{promoAmount.toLocaleString()}
            </div>
          </div>
        )}
      </div>

      <div className="w-full h-[1px] bg-gray-200 mb-3 mt-4"></div>

      {/* FARE BREAKDOWN SECTION */}
      <div className="mb-3">
        <h5 className="text-[18px] font-semibold text-[#2d3e50] mb-5">Fare Breakdown</h5>

        <div className="space-y-6">
          {fareBreakdown.map((item, idx) => {
            const count = item.PassengerCount;
            const categoryOtherCharge = perPaxOtherCharge * count;
            const totalForCategory = item.BaseFare + item.Tax + categoryOtherCharge;
            const perPaxTotal = totalForCategory / count;

            return (
              <div key={idx} className="flex justify-between items-center group">
                <div className="text-[17px] font-semibold text-[#2d3e50]">
                  {getPassengerLabelPlural(item.PassengerType, count)} ({count})
                </div>
                <div className="text-right flex items-center gap-2">
                  <span className="text-[16px] text-gray-500 font-medium">
                    ₹{perPaxTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })} × {count} =
                  </span>
                  <span className="text-[17px] font-semibold text-[#2d3e50] whitespace-nowrap">
                    ₹ {totalForCategory.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Discount if exists */}
          {fare?.Discount > 0 && (
            <div className="flex justify-between items-center group">
              <div className="text-[17px] font-bold text-green-600">Discount</div>
              <div className="text-[17px] font-bold text-green-600">
                -₹ {fare.Discount.toLocaleString()}
              </div>
            </div>
          )}

          {/* Promo Code Discount (Show here only if NO Platform Fee) */}
          {promoAmount > 0 && (!fareQuoteData?.rc_platform_fee || fareQuoteData.rc_platform_fee <= 0) && (
            <div className="group mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-[#fcf1e4] text-[#a66f36] px-2 py-[2px] rounded-md flex items-center gap-1 border border-[#fae5cd] text-[13px] font-bold tracking-wide">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="9" y1="15" x2="15" y2="9"></line>
                      <circle cx="9" cy="9" r="1.5" fill="currentColor" stroke="none"></circle>
                      <circle cx="15" cy="15" r="1.5" fill="currentColor" stroke="none"></circle>
                    </svg>
                    {promoCode || `PROMO`}
                  </div>
                  <div className="text-[15px] font-medium text-gray-700">
                    Promo discount
                  </div>
                </div>
                <div className="text-[16px] font-bold text-[#428514]">
                  -₹{promoAmount.toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {(fareQuoteData?.rc_platform_fee > 0) && (
        <div className="space-y-3 mb-3 pt-2 border-t border-gray-200">
          <div>
            <div className="flex justify-between text-[15px] font-medium text-gray-600">
              <span>Platform Service Fee</span>
              <span>₹ {fareQuoteData.rc_platform_fee.toLocaleString()}</span>
            </div>

            {/* Promo Code Discount (Show here if Platform Fee > 0) */}
            {promoAmount > 0 && (
              <div className="group mt-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#fcf1e4] text-[#a66f36] px-2 py-[2px] rounded-md flex items-center gap-1 border border-[#fae5cd] text-[13px] font-bold tracking-wide">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="9" y1="15" x2="15" y2="9"></line>
                        <circle cx="9" cy="9" r="1.5" fill="currentColor" stroke="none"></circle>
                        <circle cx="15" cy="15" r="1.5" fill="currentColor" stroke="none"></circle>
                      </svg>
                      {promoCode || `PROMO`}
                    </div>
                    <div className="text-[15px] font-medium text-gray-700">
                      Promo discount
                    </div>
                  </div>
                  <div className="text-[16px] font-bold text-[#428514]">
                    -₹{promoAmount.toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>



          {fareQuoteData.rc_gst_rate > 0 && (
            <div className="flex justify-between text-[15px] font-medium text-gray-600">
              <span>GST ({fareQuoteData.rc_gst_rate}%)</span>
              <span><span className="text-green-600 font-bold mr-1">+</span>₹ {Number(displayGstAmount.toFixed(2)).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      <div className="w-full h-[1px] bg-gray-200 mb-3"></div>

      {/* TOTAL FARE SUMMARY */}
      <div className="flex justify-between items-center mb-8">
        <div className="text-[18px] font-bold text-[#2d3e50]">Total Fare</div>
        <div className="text-[20px] font-bold text-[#2d3e50]">
          ₹ {finalTotalFare.toLocaleString()}
        </div>
      </div>

      {/* VIEW TAX DETAILS TOGGLE */}
      <div className="bg-[#f8fbff] -mx-8 -mb-8 mt-4 rounded-b-xl overflow-hidden border-t border-gray-100">
        {/* <button
          onClick={() => setShowTaxDetails(!showTaxDetails)}
          className="w-full p-5 flex items-center justify-between text-[#2d3e50] font-bold hover:bg-[#f0f7ff] transition-colors"
        >
          <span className="text-[16px] text-blue-600 group flex items-center hover:underline">
            View tax details
            <svg
              className={`ml-2 w-4 h-4 transition-transform ${showTaxDetails ? "rotate-90" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </button> */}

        {showTaxDetails && (
          <div className="px-8 pb-8 pt-2 space-y-3 bg-[#f8fbff]">
            <div className="flex justify-between text-[15px] font-medium text-gray-600">
              <span>Airline Taxes & Fees</span>
              <span>₹ {(fareBreakdown.reduce((s, i) => s + i.Tax, 0)).toLocaleString()}</span>
            </div>


          </div>
        )}
      </div>
    </div>
  );
}

FareDetails.propTypes = {
  fareQuoteData: PropTypes.object,
};

export default FareDetails;
